/**
 * "Clear After" for the status message: the options the dialog offers, and the
 * time each one means.
 *
 * The expiry is stored and enforced server-side — signaling drops a lapsed
 * message on read and each live socket clears it from its presence meta on a
 * timer — so this module only turns a choice into a time, and back again when
 * the dialog reopens on a message that already has one.
 */

export type ClearAfter = 'today' | '1h' | '2h' | 'never' | 'custom'

export const CLEAR_AFTER_OPTIONS: { value: ClearAfter; labelKey: string }[] = [
  { value: 'today', labelKey: 'presence.clearAfterToday' },
  { value: '1h', labelKey: 'presence.clearAfterOneHour' },
  { value: '2h', labelKey: 'presence.clearAfterTwoHours' },
  { value: 'never', labelKey: 'presence.clearAfterNever' },
  { value: 'custom', labelKey: 'presence.clearAfterCustom' },
]

/** Minute granularity of the custom time picker. */
export const CLEAR_AFTER_TIME_STEP = 5

const HOUR_MS = 60 * 60 * 1000

export interface ClearAfterState {
  choice: ClearAfter
  /** Custom date (only the day is read) — used when `choice` is 'custom'. */
  date?: Date
  /** Custom time, "HH:mm" 24-hour — used when `choice` is 'custom'. */
  time?: string
}

const pad = (n: number) => String(n).padStart(2, '0')

/** A day plus an "HH:mm" time, in local time. Null while either is missing. */
export function combineDateAndTime(date: Date | undefined, time: string | undefined): Date | null {
  if (!date || !time) return null
  const [h, m] = time.split(':').map(Number)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  const at = new Date(date)
  at.setHours(h, m, 0, 0)
  return at
}

/**
 * When a message saved at `now` should clear itself.
 *
 * `null` means never. `undefined` means there is nothing valid to save: the
 * custom date or time is missing, or is not in the future — a message that
 * expires the moment it is set would be a clear dressed up as a save, and the
 * server refuses it.
 *
 * "Today" is the last millisecond of the local day, not a fixed number of hours.
 */
export function resolveClearAfter(state: ClearAfterState, now: Date): Date | null | undefined {
  switch (state.choice) {
    case 'today': {
      const end = new Date(now)
      end.setHours(23, 59, 59, 999)
      return end
    }
    case '1h':
      return new Date(now.getTime() + HOUR_MS)
    case '2h':
      return new Date(now.getTime() + 2 * HOUR_MS)
    case 'never':
      return null
    case 'custom': {
      const at = combineDateAndTime(state.date, state.time)
      return at && at.getTime() > now.getTime() ? at : undefined
    }
  }
}

/**
 * The custom date and time offered when "Custom Date & Time" is first picked:
 * an hour from now, rounded up to the picker's step, so it starts out valid.
 */
export function defaultCustomClearAfter(now: Date): { date: Date; time: string } {
  const at = new Date(now.getTime() + HOUR_MS)
  const minutes = at.getHours() * 60 + at.getMinutes()
  const snapped = Math.ceil(minutes / CLEAR_AFTER_TIME_STEP) * CLEAR_AFTER_TIME_STEP
  at.setHours(0, snapped, 0, 0) // setHours normalizes 60+ minutes into the next hour/day
  const date = new Date(at)
  date.setHours(0, 0, 0, 0)
  return { date, time: `${pad(at.getHours())}:${pad(at.getMinutes())}` }
}

/** Which option produced a saved clear time — see `rememberClearAfter`. */
export interface RememberedClearAfter {
  expiresAt: string
  choice: ClearAfter
}

const MEMORY_KEY = 'inmeet:status-clear-after'

/** Options worth reopening on by name; Custom is rebuilt from the time itself. */
const REOPEN_BY_NAME: ReadonlySet<ClearAfter> = new Set(['today', '1h', '2h'])

/**
 * Remember which option produced the clear time just saved, so the dialog can
 * reopen on "1 Hour" rather than on the raw time. The server stores only the
 * time, so this is a per-device note: a message saved elsewhere (another
 * browser, the mobile app) has no matching note and reopens on Custom Date &
 * Time instead. `null` (Never, or no message) forgets it.
 */
export function rememberClearAfter(expiresAt: string | null, choice: ClearAfter): void {
  try {
    if (expiresAt) localStorage.setItem(MEMORY_KEY, JSON.stringify({ expiresAt, choice }))
    else localStorage.removeItem(MEMORY_KEY)
  } catch {
    // Storage unavailable (private window, blocked): the dialog reopens on the time.
  }
}

/** The note `rememberClearAfter` left, or null. */
export function recallClearAfter(): RememberedClearAfter | null {
  try {
    const raw = localStorage.getItem(MEMORY_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<RememberedClearAfter>
    return typeof v?.expiresAt === 'string' && typeof v.choice === 'string'
      ? { expiresAt: v.expiresAt, choice: v.choice }
      : null
  } catch {
    return null
  }
}

/**
 * What the dialog should show when it opens on the message already set.
 *
 * A message with an expiry reopens on the option that set it ("1 Hour") when
 * `remembered` is a note for exactly that expiry. The note is trusted only on
 * an exact match, so a message changed since — on another device, or by its
 * own expiry — never reopens on a stale option; it falls back to Custom Date &
 * Time holding the time. Saving a reopened "1 Hour" counts the hour from that
 * save, as picking it does.
 *
 * A message with no expiry reopens on "Never". With no message the dialog
 * starts on "Today".
 */
export function clearAfterFor(
  message: string,
  expiresAt: string | null | undefined,
  remembered: RememberedClearAfter | null = null,
): ClearAfterState {
  if (!message) return { choice: 'today' }
  const at = expiresAt ? new Date(expiresAt) : null
  if (!at || Number.isNaN(at.getTime())) return { choice: 'never' }
  if (
    remembered &&
    REOPEN_BY_NAME.has(remembered.choice) &&
    Date.parse(remembered.expiresAt) === at.getTime()
  ) {
    return { choice: remembered.choice }
  }
  const date = new Date(at)
  date.setHours(0, 0, 0, 0)
  return { choice: 'custom', date, time: `${pad(at.getHours())}:${pad(at.getMinutes())}` }
}

/** Two states that mean the same choice compare equal — drives the Save button. */
export function sameClearAfter(a: ClearAfterState, b: ClearAfterState): boolean {
  if (a.choice !== b.choice) return false
  if (a.choice !== 'custom') return true
  return a.date?.toDateString() === b.date?.toDateString() && a.time === b.time
}

/**
 * The expiry as the settings row shows it: the time alone when it is today,
 * otherwise the date too.
 */
export function formatClearsAt(expiresAt: string, now: Date): string {
  const at = new Date(expiresAt)
  const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  if (at.toDateString() === now.toDateString()) return time
  const day = at.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(at.getFullYear() !== now.getFullYear() ? { year: 'numeric' as const } : {}),
  })
  return `${day}, ${time}`
}
